import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, ArrowRight } from 'lucide-react';
// import PRTack_Logo_Transparent_OK from '@/assets/PRTack_Logo_Transparent_OK.png';
import PringLogo from '../../assets/Pring_Logo_black.png';

import { authService } from '@/services/api/services/authService';
import { RequiredPasswordChangeModal } from '@/components/features/auth/RequiredPasswordChangeModal';

export const AccountConfirmation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'awaitingPassword' | 'success' | 'error'>(
    'loading'
  );
  const [message, setMessage] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    const bootstrap = async () => {
      if (!token) {
        setStatus('error');
        setMessage(t('pages.accountConfirmation.errors.noToken'));
        return;
      }

      // Validate token on load
      const validation = await authService.validateConfirmationToken({ token });
      if (!validation.success) {
        setStatus('error');
        setMessage(t('pages.accountConfirmation.errors.confirmationFailed'));
        return;
      }

      const v = validation.data;
      if (v.status === 'invalid') {
        setStatus('error');
        setMessage(t('pages.accountConfirmation.error.invalidToken'));
        return;
      }
      if (v.status === 'expired') {
        setStatus('error');
        setMessage(t('pages.accountConfirmation.error.expiredToken'));
        return;
      }
      if (v.status === 'already_confirmed') {
        setStatus('success');
        setMessage(t('pages.accountConfirmation.success.alreadyConfirmed'));
        return;
      }

      // valid
      if (v.requiresPasswordSetup) {
        setStatus('awaitingPassword');
      } else {
        // If password already set but not confirmed, we can directly confirm
        setStatus('awaitingPassword');
      }
    };

    bootstrap();
  }, [token, t]);

  const handleSetPassword = async (newPassword: string) => {
    if (!token) return;
    setIsSavingPassword(true);
    try {
      // 1) Set password using confirmation token (no auth required)
      const setPwdResp = await authService.setPasswordWithConfirmation({
        token,
        newPassword: newPassword,
      });
      if (!setPwdResp.success) {
        setStatus('error');
        setMessage(
          setPwdResp.error.message || t('pages.accountConfirmation.errors.confirmationFailed')
        );
        return;
      }

      // 2) Confirm account
      const confirmResp = await authService.confirmAccount({ token });
      if (!confirmResp.success) {
        setStatus('error');
        setMessage(
          confirmResp.error.message || t('pages.accountConfirmation.errors.confirmationFailed')
        );
        return;
      }

      // 3) Success → show success screen with button to go to login
      setStatus('success');
      setMessage(t('pages.accountConfirmation.success.message'));
    } catch {
      setStatus('error');
      setMessage(t('pages.accountConfirmation.errors.confirmationFailed'));
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header negro */}
        <div className="bg-black px-8 pt-8 pb-4 flex flex-col items-center">
          <img src={PringLogo} alt="Pring Logo" className="h-20 w-auto mb-2" />
          <h2 className="text-2xl font-extrabold text-white text-center tracking-tight">
            {t('common.appTitle')}
          </h2>
        </div>
        <div className="p-8 flex flex-col items-center">
          {status === 'loading' && (
            <div className="space-y-4 w-full text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-orange mx-auto"></div>
              <h2 className="text-2xl font-bold text-brand-gray-dark">
                {t('pages.accountConfirmation.loading.title')}
              </h2>
              <p className="text-brand-gray-light">
                {t('pages.accountConfirmation.loading.message')}
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-6 w-full text-center">
              <div className="flex justify-center">
                <CheckCircle className="h-20 w-20 text-green-500 mx-auto" />
              </div>
              <h2 className="text-3xl font-bold text-brand-gray-dark">
                {t('pages.accountConfirmation.success.title')}
              </h2>
              <p className="text-lg text-brand-gray-light max-w-md mx-auto">{message}</p>
              <div className="pt-4">
                <button
                  onClick={handleGoToLogin}
                  className="w-full bg-brand-orange text-white px-8 py-3 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  {t('pages.accountConfirmation.success.goToLogin')}
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6 w-full text-center">
              <div className="flex justify-center">
                <XCircle className="h-20 w-20 text-red-500 mx-auto" />
              </div>
              <h2 className="text-3xl font-bold text-brand-gray-dark">
                {t('pages.accountConfirmation.error.title')}
              </h2>
              <p className="text-lg text-brand-gray-light max-w-md mx-auto">{message}</p>
              <div className="pt-4">
                <button
                  onClick={handleGoToLogin}
                  className="w-full bg-brand-orange text-white px-8 py-3 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  {t('pages.accountConfirmation.error.goToLogin')}
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Force password setup modal */}
      <RequiredPasswordChangeModal
        isOpen={status === 'awaitingPassword'}
        onConfirm={handleSetPassword}
        isLoading={isSavingPassword}
        mode="account-confirmation"
      />
    </div>
  );
};
